import { useState, useEffect, useCallback, Fragment } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import StoriesBar from '../components/stories/StoriesBar';
import PostCard from '../components/post/PostCard';
import CreatePostModal from '../components/post/CreatePostModal';
import { StatusComposer, ReelsStrip, FollowSuggestions, AdSlotCard } from '../components/social/discovery';
import { Skeleton } from '../components/ui/skeleton';
import { RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function HomePage() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [createAsStory, setCreateAsStory] = useState(false);

  const fetchFeed = useCallback(async (pageNum = 0, append = false) => {
    try {
      const [feedRes, storiesRes] = await Promise.all([
        api.get(`/posts/feed?skip=${pageNum * 20}&limit=20`),
        pageNum === 0 ? api.get('/posts/stories') : Promise.resolve({ data: stories }),
      ]);
      setPosts((prev) => (append ? [...prev, ...feedRes.data] : feedRes.data));
      if (pageNum === 0) setStories(storiesRes.data);
      setHasMore(feedRes.data.length === 20);
    } catch (error) {
      console.error('Error fetching feed:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [stories]);

  useEffect(() => { fetchFeed(); }, [fetchFeed]);

  const loadMore = () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    const nextPage = page + 1;
    setPage(nextPage);
    fetchFeed(nextPage, true);
  };

  const handlePostUpdate = (postId, updates) =>
    setPosts((prev) => prev.map((p) => (p.id === postId ? { ...p, ...updates } : p)));
  const handlePostDelete = (postId) =>
    setPosts((prev) => prev.filter((p) => p.id !== postId));
  const handleRefresh = () => { setLoading(true); setPage(0); fetchFeed(0, false); };

  const handlePostCreated = useCallback((newPost) => {
    if (!newPost?.id) return;
    if (newPost.is_story) {
      // Refresh stories strip after creating a story
      api.get('/posts/stories').then(r => setStories(r.data)).catch(() => {});
      return;
    }
    setPosts((prev) => {
      if (prev.some((p) => p.id === newPost.id)) return prev;
      return [newPost, ...prev];
    });
  }, []);

  return (
    <div className="min-[900px]:ml-60 px-4" data-testid="home-page">
      <div className="max-w-2xl mx-auto pt-4 space-y-5 pb-8">
          {/* Status composer — real create-post workflow */}
          <StatusComposer user={user} onOpen={() => setShowCreate(true)} />

          {/* Stories */}
          <StoriesBar
            stories={stories}
            currentUser={user}
            onAddStory={() => { setCreateAsStory(true); setShowCreate(true); }}
            onStoriesChange={() => api.get('/posts/stories').then(r => setStories(r.data)).catch(() => {})}
          />

          {/* Reels / short-video discovery strip (real data) */}
          <ReelsStrip />

          {/* Feed header */}
          <div className="flex items-center justify-between">
            <h1 className="font-heading text-2xl font-bold">For You</h1>
            <Button
              variant="ghost" size="icon" onClick={handleRefresh} disabled={loading}
              className="rounded-full" data-testid="refresh-feed-btn"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          {/* Mixed social feed */}
          <div className="space-y-5">
            {loading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="mg-card-elevated p-4 space-y-4">
                  <div className="flex items-center gap-3">
                    <Skeleton className="w-11 h-11 rounded-full" />
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-24" />
                      <Skeleton className="h-3 w-16" />
                    </div>
                  </div>
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-64 w-full rounded-xl" />
                </div>
              ))
            ) : posts.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
                  <RefreshCw className="w-8 h-8 text-muted-foreground" />
                </div>
                <h3 className="font-heading text-xl font-bold mb-2">Nothing here yet</h3>
                <p className="text-muted-foreground">Be the first to share something with Midgard.</p>
              </div>
            ) : (
              <>
                {posts.map((post, index) => (
                  <Fragment key={post.id}>
                    <PostCard
                      post={post}
                      onUpdate={handlePostUpdate}
                      onDelete={handlePostDelete}
                      style={{ animationDelay: `${index * 0.05}s` }}
                    />
                    {/* Occasional follow-suggestion unit */}
                    {index === 2 && <FollowSuggestions limit={4} title="Suggested for You" />}
                    {/* Occasional sponsored unit (~1 per 7 organic posts) */}
                    {index > 0 && (index + 1) % 7 === 0 && <AdSlotCard />}
                  </Fragment>
                ))}

                {hasMore && (
                  <div className="text-center py-4">
                    <Button
                      variant="outline" onClick={loadMore} disabled={loadingMore}
                      className="rounded-full" data-testid="load-more-btn"
                    >
                      {loadingMore ? (
                        <span className="flex items-center gap-2">
                          <span className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                          Loading...
                        </span>
                      ) : 'Load More'}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

      <CreatePostModal
        open={showCreate}
        onOpenChange={(v) => { setShowCreate(v); if (!v) setCreateAsStory(false); }}
        onPostCreated={handlePostCreated}
        defaultIsStory={createAsStory}
      />
    </div>
  );
}
